"use client"

import { useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { Download, FileText, Settings } from "lucide-react"
import type { ExportOptions } from "@/lib/export-utils"

interface ExportModalProps {
  onExportCSV: (options: ExportOptions) => void
  onExportPDF: (options: ExportOptions) => void
}

// Uses shared ExportOptions type

export function ExportModal({ onExportCSV, onExportPDF }: ExportModalProps) {
  const [options, setOptions] = useState<ExportOptions>({
    includeSummary: true,
    includePeerGroup: true,
    includeDiscountMatrix: true,
    includeAnalysis: true,
    includeParameters: true,
  })

  const handleOptionChange = (key: keyof ExportOptions, value: boolean) => {
    setOptions((prev) => ({ ...prev, [key]: value }))
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Settings className="h-4 w-4 mr-2" />
          Export Options
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Export Configuration</DialogTitle>
          <DialogDescription>Choose which sections to include in your export</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="summary"
                checked={options.includeSummary}
                onCheckedChange={(checked) => handleOptionChange("includeSummary", checked as boolean)}
              />
              <Label htmlFor="summary">DLOM Summary</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="parameters"
                checked={options.includeParameters}
                onCheckedChange={(checked) => handleOptionChange("includeParameters", checked as boolean)}
              />
              <Label htmlFor="parameters">Calculation Parameters</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="peer-group"
                checked={options.includePeerGroup}
                onCheckedChange={(checked) => handleOptionChange("includePeerGroup", checked as boolean)}
              />
              <Label htmlFor="peer-group">Peer Group Analysis</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="discount-matrix"
                checked={options.includeDiscountMatrix}
                onCheckedChange={(checked) => handleOptionChange("includeDiscountMatrix", checked as boolean)}
              />
              <Label htmlFor="discount-matrix">Discount Matrix</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="analysis"
                checked={options.includeAnalysis}
                onCheckedChange={(checked) => handleOptionChange("includeAnalysis", checked as boolean)}
              />
              <Label htmlFor="analysis">Key Insights & Analysis</Label>
            </div>
          </div>

          <Separator />

          <div className="flex space-x-2">
            <Button onClick={() => onExportCSV(options)} className="flex-1">
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button onClick={() => onExportPDF(options)} variant="outline" className="flex-1">
              <FileText className="h-4 w-4 mr-2" />
              Export PDF
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
